import React, { useState, useEffect } from 'react';
import { Box, Typography, CircularProgress } from '@mui/material';
import { BrokenImage } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { moduleService } from '@/services';

interface TrolleyShelterLocationImageViewerProps {
    imagePath: string | null;
}

const TrolleyShelterLocationImageViewer: React.FC<TrolleyShelterLocationImageViewerProps> = ({ imagePath }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(!!imagePath);
    const [imageData, setImageData] = useState<ArrayBuffer | null>(null);
    const [loadedPath, setLoadedPath] = useState<string | null>(null);

    useEffect(() => {
        loadImage();
    }, [imagePath]);

    const loadImage = async () => {
        if (!imagePath) {
            setImageData(null);
            setLoadedPath(null);
            return;
        }

        setLoading(true);
        try {
            const response = await moduleService.loadTrolleyShelterLocationImage(imagePath as string);
            setImageData(response);
        } catch (error) {
            setImageData(null);
        } finally {
            setLoadedPath(imagePath);
            setLoading(false);
        }
    };

    const getImageBlobUrl = (): string | null => {
        if (!imageData) return null;
        const blob = new Blob([imageData], { type: 'image/*' });
        return URL.createObjectURL(blob);
    };

    const imageBlobUrl = getImageBlobUrl();

    if (loading) {
        return (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                <CircularProgress />
            </Box>
        );
    }

    if (!loading && imageData && imageBlobUrl) {
        return (
            <Box sx={{ width: '100%' }}>
                <img src={imageBlobUrl} alt="Trolley Shelter Location" style={{ width: '100%', height: 'auto' }} />
            </Box>
        );
    }

    if (!loading && !imageData && loadedPath === imagePath && imagePath) {
        return (
            <Box sx={{ display: 'flex', alignItems: 'center', color: 'error.main', mt: 2 }}>
                <BrokenImage sx={{ mr: 1 }} />
                <Typography color="error">{t('imageMissingFromStorage')}</Typography>
            </Box>
        );
    }

    return null;
};

export default TrolleyShelterLocationImageViewer;
